import {
  Security as SecurityIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Public as PublicIcon,
  Business as BusinessIcon,
  Lock as LockIcon,
  People as PeopleIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import {
  Box, Typography, Chip, Card, CardContent, Avatar
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { AccessCheckResult, BodyUpdateDocumentPermissionsApiDocumentsDocumentIdPermissionsPut } from '../../../api/ApiClient';

type PermissionUpdate = Partial<BodyUpdateDocumentPermissionsApiDocumentsDocumentIdPermissionsPut>;

interface AccessInfoCardProps {
  accessInfo: AccessCheckResult;
  permissions: PermissionUpdate;
  // Additional details for better display
  sharedUsersDetails?: Array<{ id: string; username: string; email: string; full_name: string }>;
  sharedGroupsDetails?: Array<{ id: string; name: string; description: string }>;
}



export const AccessInfoCard: React.FC<AccessInfoCardProps> = ({
  accessInfo,
  permissions,
  sharedUsersDetails = [],
  sharedGroupsDetails = []
}) => {
  const { t } = useTranslation();
  
  const getVisibilityInfo = (visibility: string) => {
    switch (visibility) {
      case 'private': return { icon: <LockIcon />, label: t('documents.visibility.private'), color: 'default' as const };
      case 'organization': return { icon: <BusinessIcon />, label: t('documents.visibility.organization'), color: 'primary' as const };
      case 'public': return { icon: <PublicIcon />, label: t('documents.visibility.public'), color: 'success' as const };
      default: return { icon: <SecurityIcon />, label: t('documents.permissions.unknown'), color: 'default' as const };
    }
  };

  const getAccessReasonDisplay = (reason: string) => {
    switch (reason) {
      case 'owner': return { icon: <PersonIcon />, label: t('documents.permissions.documentOwner'), color: 'primary' as const };
      case 'user_shared': return { icon: <PersonIcon />, label: t('documents.permissions.sharedWithYou'), color: 'info' as const };
      case 'group_shared': return { icon: <GroupIcon />, label: t('documents.permissions.sharedViaGroup'), color: 'info' as const };
      case 'role_shared': return { icon: <AdminIcon />, label: t('documents.permissions.sharedViaRole'), color: 'info' as const };
      case 'organization': return { icon: <BusinessIcon />, label: t('documents.permissions.organizationAccess'), color: 'success' as const };
      case 'public': return { icon: <PublicIcon />, label: t('documents.permissions.publicDocument'), color: 'success' as const };
      default: return { icon: <SecurityIcon />, label: t('documents.permissions.unknownAccess'), color: 'default' as const };
    }
  };
  const reasonInfo = getAccessReasonDisplay(accessInfo.access_reason);
  const visibilityInfo = getVisibilityInfo(permissions.visibility || 'private');

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
            <SecurityIcon />
          </Avatar>
          <Box>
            <Typography variant="h6">
              {t('documents.permissions.accessInfo')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('documents.permissions.currentPermissions')}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip
            icon={reasonInfo.icon}
            label={reasonInfo.label}
            color={reasonInfo.color}
            size="small"
          />
          <Chip
            icon={visibilityInfo.icon}
            label={visibilityInfo.label}
            color={visibilityInfo.color}
            size="small"
          />
          {accessInfo.is_owner && (
            <Chip
              icon={<PersonIcon />}
              label={t('documents.permissions.owner')}
              color="primary"
              size="small"
              variant="outlined"
            />
          )}
          {accessInfo.can_modify_permissions && (
            <Chip
              icon={<SecurityIcon />}
              label={t('documents.permissions.canEdit')}
              color="success"
              size="small"
              variant="outlined"
            />
          )}
        </Box>

        {/* Additional sharing info */}
        {sharedUsersDetails && sharedUsersDetails.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1, fontSize: 16 }} />
              {t('documents.permissions.sharedWithUsers')}: {sharedUsersDetails.map(user => user.full_name || user.username).join(', ')}
            </Typography>
          </Box>
        )}

        {sharedGroupsDetails && sharedGroupsDetails.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
              <PeopleIcon sx={{ mr: 1, fontSize: 16 }} />
              {t('documents.permissions.sharedWithGroups')}: {sharedGroupsDetails.map(group => group.name).join(', ')}
            </Typography>
          </Box>
        )}

        {permissions.shared_with_roles && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
              <AdminIcon sx={{ mr: 1, fontSize: 16 }} />
              {t('documents.permissions.sharedWithRoles')}: {permissions.shared_with_roles}
            </Typography>
          </Box>
        )}

        {permissions.retention_days && permissions.retention_days > 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary">
              {t('documents.permissions.retention', { days: permissions.retention_days })}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};